import { Chain } from '@ankr.com/chains-list';
import { Typography } from '@mui/material';

import { ChainRequestsLabel } from 'domains/chains/components/ChainRequestsLabel';
import { checkIsArchive } from 'modules/chains/utils/isArchive';
import { hasPublicSubchains } from 'modules/chains/utils/hasPublicSubchains';
import { selectNodesDetails } from 'modules/chains/store/selectors';
import { useAccountStatus } from 'modules/accountStatus/hooks/useAccountStatus';
import { useAppSelector } from 'store/useAppSelector';
import { useAuth } from 'modules/auth/hooks/useAuth';

import { ChainLogo } from '../ChainLogo';
import { useChainDescriptionStyles } from './useChainDescriptionStyles';
import { useChainStatuses } from './useChainStatuses';
import { ChainLabels } from './components/ChainLabels';

interface IChainDescriptionProps {
  className?: string;
  classNameChainName?: string;
  chain: Chain;
  logoSize?: number;
  subchainLabels?: string[];
  isCompactView?: boolean;
  isStatusHidden?: boolean; // if true chain status tooltip is not visible
}

export const ChainDescription = ({
  chain,
  className,
  classNameChainName,
  isCompactView,
  isStatusHidden,
  logoSize,
  subchainLabels,
}: IChainDescriptionProps) => {
  const { isLoggedIn } = useAuth();
  const { isEnterprise, isPremium } = useAccountStatus();

  const { coinName, id, name } = chain;

  const { data: nodes = [] } = useAppSelector(selectNodesDetails);

  const isArchive = checkIsArchive(nodes, id);

  const hasChainLabels = subchainLabels && subchainLabels?.length > 0;

  const hasPremiumLabel = chain.premiumOnly && !isPremium && !isEnterprise;

  const hasFreemiumLabel =
    !hasPremiumLabel && !hasPublicSubchains(chain) && !isLoggedIn;

  const { currentChainStatuses, hasChainStatus } = useChainStatuses(
    chain,
    isLoggedIn,
  );

  const isStatusVisible = hasChainStatus && !isStatusHidden && isLoggedIn;

  const hasLabels =
    hasPremiumLabel ||
    hasFreemiumLabel ||
    isArchive ||
    hasChainLabels ||
    isStatusVisible;

  const { classes, cx } = useChainDescriptionStyles();

  return (
    <div className={cx(classes.chainDescriptionRoot, className)}>
      <ChainLogo chain={chain} size={logoSize} />
      <div className={classes.description}>
        <h1 className={classes.top}>
          <Typography
            variant="subtitle1"
            color="textPrimary"
            className={cx(
              classes.chainName,
              {
                [classes.chainNameSmall]: isCompactView,
              },
              classNameChainName,
            )}
          >
            {name}
          </Typography>
          <ChainRequestsLabel
            descriptionClassName={cx(classes.coinName, {
              [classes.coinNameSmall]: isCompactView,
            })}
            description={coinName}
            descriptionColor="textSecondary"
          />
        </h1>

        {hasLabels && (
          <div className={classes.chips}>
            <ChainLabels
              currentChainStatuses={currentChainStatuses}
              hasFreemiumLabel={hasFreemiumLabel}
              hasPremiumLabel={hasPremiumLabel}
              isArchive={isArchive}
              isStatusVisible={isStatusVisible}
              subchainLabels={subchainLabels}
            />
          </div>
        )}
      </div>
    </div>
  );
};
